/**
 * Compare surfaces — Advanced settings (spec §4.2): one subsection per ticked
 * result, collapsed by default and opened by a validation error inside it.
 *
 *  - **Cut & fill:** fill gaps and the heat-map colour ramp.
 *  - **Deviation map:** reference mode (surfaces / TIN), decimate, k-nearest
 *    triangles, vertical threshold (horizontal mode), signed (nearest mode),
 *    IFC name / types (IFC design), the point-cloud design's DSM settings,
 *    the GeoTIFF's resolution / statistic / SRID, the renders' image size and
 *    ramp, and — for a deviation-only run, the only one whose m³ are shown
 *    (§10 decision 4) — the volume grid.
 *
 * Tolerance and the colour range are not here: they are Settings.
 */
import { useFormState, useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import type { LayerData } from '@/atoms/layers.atom';
import { AdvancedSection } from '@/components/analyses/sections';
import { UnitSuffix } from '@/components/compare/UnitSuffix';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isIfcModelLayer, isPointcloudLayer } from '@/lib/analyses/candidates';
import type { CompareFormValues } from '@/lib/analyses/compare/formSchema';
import { matplotlibRampOptions } from '@/lib/colormaps';

/** Cut is red in both (T4): plain spellings for `dz = top − bottom`, `_r` ones for a deviation. */
const CUT_FILL_RAMPS = matplotlibRampOptions('surfaceDifference');
const DEVIATION_RAMPS = matplotlibRampOptions('deviation');

/** The deviation fields whose errors open Advanced (the others are Settings). */
const DEVIATION_ADVANCED_FIELDS = [
  'decimate',
  'verticalThreshold',
  'kNearestTriangles',
  'tifResolution',
  'tifSrid',
  'renderImageSize',
  'volumeResolution',
  'referenceResolution',
] as const;

/** A re-run's value that is no longer on the list is kept as an option: a
 *  Select whose value is not among its items renders empty, which reads as
 *  "no ramp set" (T4 dropped `jet`). */
const rampsWith = (ramps: { value: string; label: string }[], value: string) =>
  ramps.some((c) => c.value === value) || !value ? ramps : [...ramps, { value, label: value }];

function Subsection({ title, testId, children }: { title: ReactNode; testId: string; children: ReactNode }) {
  return (
    <fieldset data-testid={testId} className='flex min-w-0 flex-col gap-3'>
      <legend className='mb-2 text-sm font-semibold'>{title}</legend>
      {/* The container the field grids below query: two columns only when
          the dialog itself has room (long labels in de / hu stay readable). */}
      <div className='@container flex min-w-0 flex-col gap-3'>{children}</div>
    </fieldset>
  );
}

export interface CompareAdvancedProps {
  form: UseFormReturn<CompareFormValues>;
  cutFillOn: boolean;
  deviationOn: boolean;
  design: LayerData | undefined;
  /** The renders will be made (ticked, or forced by the report). */
  rendersOn: boolean;
}

export function CompareAdvanced({ form, cutFillOn, deviationOn, design, rendersOn }: CompareAdvancedProps) {
  const { t } = useTranslation('project');
  const [mode, geotiff, volumeStats] = useWatch({
    control: form.control,
    name: ['deviation.mode', 'addOns.geotiff', 'deviation.volumeStats'],
  });
  const { errors } = useFormState({ control: form.control });
  const hasError =
    (cutFillOn && !!(errors.cutFill?.fillHoles || errors.cutFill?.colormap)) ||
    (deviationOn && DEVIATION_ADVANCED_FIELDS.some((f) => !!errors.deviation?.[f]));

  const designIsCloud = !!design && isPointcloudLayer(design);
  const designIsIfc = isIfcModelLayer(design);
  const control = form.control;

  if (!cutFillOn && !deviationOn) return null;

  return (
    <AdvancedSection testId='compare-advanced' hasError={hasError}>
      {cutFillOn && (
        <Subsection title={t('compareSurfaces.part.cutFill')} testId='compare-advanced-cutFill'>
          <FormField
            control={control}
            name='cutFill.fillHoles'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('surfaceDifference.fillHoles')}</FormLabel>
                <FormControl>
                  <NumericField value={field.value} onChange={field.onChange} step='1' min='0' />
                </FormControl>
                <FormDescription>{t('surfaceDifference.fillHolesHelp')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name='cutFill.colormap'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('surfaceDifference.colormap')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {rampsWith(CUT_FILL_RAMPS, field.value).map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </Subsection>
      )}

      {deviationOn && (
        <Subsection title={t('compareSurfaces.part.deviation')} testId='compare-advanced-deviation'>
          {/* One continuous TIN or N independent surfaces; follows the design
              until the user picks (a point-cloud design is always a TIN). */}
          {!designIsCloud && (
            <FormField
              control={control}
              name='deviation.referenceMode'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('deviationAnalysis.referenceMode')}</FormLabel>
                  <Select
                    onValueChange={(v) => {
                      field.onChange(v);
                      form.setValue('deviation.referenceModeEdited', true, { shouldDirty: true });
                    }}
                    value={field.value}>
                    <FormControl>
                      <SelectTrigger className='w-full'>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='surfaces'>{t('deviationAnalysis.referenceModes.surfaces')}</SelectItem>
                      <SelectItem value='tin'>{t('deviationAnalysis.referenceModes.tin')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {field.value === 'surfaces'
                      ? t('deviationAnalysis.referenceModeHelp.surfaces')
                      : t('deviationAnalysis.referenceModeHelp.tin')}
                  </FormDescription>
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name='deviation.decimate'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('deviationAnalysis.decimate')}</FormLabel>
                <FormControl>
                  <NumericField value={field.value} onChange={field.onChange} step='1' min='1' />
                </FormControl>
                <FormDescription>{t('deviationAnalysis.decimateHelp')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {mode === 'horizontal' && (
            <FormField
              control={control}
              name='deviation.verticalThreshold'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('deviationAnalysis.verticalThreshold')}</FormLabel>
                  <FormControl>
                    <NumericField value={field.value} onChange={field.onChange} step='0.05' min='0' max='1' />
                  </FormControl>
                  <FormDescription>{t('deviationAnalysis.verticalThresholdHelp')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={control}
            name='deviation.kNearestTriangles'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('deviationAnalysis.kNearestTriangles')}</FormLabel>
                <FormControl>
                  <NumericField value={field.value} onChange={field.onChange} step='1' min='1' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {mode === 'nearest' && (
            <FormField
              control={control}
              name='deviation.signed'
              render={({ field }) => (
                <FormItem className='flex items-center gap-2'>
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(!!c)} />
                  </FormControl>
                  <FormLabel className='!mt-0'>{t('deviationAnalysis.signed')}</FormLabel>
                </FormItem>
              )}
            />
          )}
          {designIsIfc && (
            <>
              <FormField
                control={control}
                name='deviation.ifcName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.ifcName')}</FormLabel>
                    <FormControl>
                      <Input type='text' placeholder={t('deviationAnalysis.ifcNamePlaceholder')} {...field} />
                    </FormControl>
                    <FormDescription>{t('deviationAnalysis.ifcNameHelp')}</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name='deviation.ifcTypes'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.ifcTypes')}</FormLabel>
                    <FormControl>
                      <Input type='text' placeholder={t('deviationAnalysis.ifcTypesPlaceholder')} {...field} />
                    </FormControl>
                    <FormDescription>{t('deviationAnalysis.ifcTypesHelp')}</FormDescription>
                  </FormItem>
                )}
              />
            </>
          )}

          {designIsCloud && (
            <div className='grid min-w-0 grid-cols-1 gap-3 @min-[420px]:grid-cols-2 border-t border-border pt-3'>
              <div className='col-span-full text-sm font-medium'>{t('deviationAnalysis.pcReferenceSection')}</div>
              <FormField
                control={control}
                name='deviation.referenceResolution'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('compareSurfaces.advanced.referenceResolution')}</FormLabel>
                    <UnitSuffix unit='m' testId='compare-reference-resolution-unit'>
                      <FormControl>
                        <NumericField value={field.value} onChange={field.onChange} step='any' className='pr-10' />
                      </FormControl>
                    </UnitSuffix>
                    <FormDescription>{t('deviationAnalysis.referenceResolutionHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name='deviation.referenceCellStat'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.referenceCellStat')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='w-full'>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='min'>{t('deviationAnalysis.referenceCellStats.min')}</SelectItem>
                        <SelectItem value='mean'>{t('deviationAnalysis.referenceCellStats.mean')}</SelectItem>
                        <SelectItem value='median'>{t('deviationAnalysis.referenceCellStats.median')}</SelectItem>
                        <SelectItem value='p05'>{t('deviationAnalysis.referenceCellStats.p05')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('deviationAnalysis.referenceCellStatHelp')}</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name='deviation.referenceInterp'
                render={({ field }) => (
                  <FormItem className='col-span-full'>
                    <FormLabel>{t('deviationAnalysis.referenceInterp')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='w-full'>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='nearest'>{t('deviationAnalysis.referenceInterps.nearest')}</SelectItem>
                        <SelectItem value='bilinear'>{t('deviationAnalysis.referenceInterps.bilinear')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('deviationAnalysis.referenceInterpHelp')}</FormDescription>
                  </FormItem>
                )}
              />
            </div>
          )}

          {geotiff && (
            <div className='grid min-w-0 grid-cols-1 gap-3 @min-[420px]:grid-cols-2 border-t border-border pt-3'>
              <div className='col-span-full text-sm font-medium'>{t('analysisConfig.addOns.exportGeotiff')}</div>
              <FormField
                control={control}
                name='deviation.tifResolution'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('compareSurfaces.advanced.tifResolution')}</FormLabel>
                    <UnitSuffix unit='m' testId='compare-tif-resolution-unit'>
                      <FormControl>
                        <NumericField value={field.value} onChange={field.onChange} step='any' className='pr-10' />
                      </FormControl>
                    </UnitSuffix>
                    <FormDescription>{t('deviationAnalysis.tifResolutionHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name='deviation.tifStat'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.tifStat')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='w-full'>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='mean'>{t('deviationAnalysis.tifStats.mean')}</SelectItem>
                        <SelectItem value='max'>{t('deviationAnalysis.tifStats.max')}</SelectItem>
                        <SelectItem value='min'>{t('deviationAnalysis.tifStats.min')}</SelectItem>
                        <SelectItem value='count'>{t('deviationAnalysis.tifStats.count')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('deviationAnalysis.tifStatHelp')}</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name='deviation.tifSrid'
                render={({ field }) => (
                  <FormItem className='col-span-full'>
                    <FormLabel>{t('deviationAnalysis.tifSrid')}</FormLabel>
                    <FormControl>
                      {/* Empty → null → the server stamps the project's SRID. */}
                      <NumericField
                        step='1'
                        min='1'
                        placeholder={t('deviationAnalysis.tifSridPlaceholder')}
                        value={field.value ?? null}
                        onChange={(n) => {
                          const i = Math.trunc(n);
                          field.onChange(i > 0 ? i : null);
                        }}
                        onClear={() => field.onChange(null)}
                      />
                    </FormControl>
                    <FormDescription>{t('deviationAnalysis.tifSridHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {rendersOn && (
            <div
              data-testid='compare-advanced-renders'
              className='grid min-w-0 grid-cols-1 gap-3 @min-[420px]:grid-cols-2 border-t border-border pt-3'>
              <div className='col-span-full text-sm font-medium'>{t('compareSurfaces.advanced.renders')}</div>
              <FormField
                control={control}
                name='deviation.renderImageSize'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.renderImageSize')}</FormLabel>
                    <FormControl>
                      <NumericField value={field.value} onChange={field.onChange} step='256' min='256' max='8192' />
                    </FormControl>
                    <FormDescription>{t('deviationAnalysis.renderImageSizeHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name='deviation.renderColormap'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.renderColormap')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='w-full'>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {rampsWith(DEVIATION_RAMPS, field.value).map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('deviationAnalysis.renderColormapHelp')}</FormDescription>
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* A paired run's m³ come from cut & fill; only a deviation-only
              run shows its own, so only it has a volume grid to set. */}
          {!cutFillOn && volumeStats && (
            <div
              data-testid='compare-deviation-volume-grid'
              className='grid min-w-0 grid-cols-1 gap-3 @min-[420px]:grid-cols-2 border-t border-border pt-3'>
              <div className='col-span-full text-sm font-medium'>{t('deviationAnalysis.volumeStats')}</div>
              <FormField
                control={control}
                name='deviation.volumeResolution'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('compareSurfaces.advanced.volumeResolution')}</FormLabel>
                    <UnitSuffix unit='m' testId='compare-volume-resolution-unit'>
                      <FormControl>
                        <NumericField value={field.value} onChange={field.onChange} step='any' className='pr-10' />
                      </FormControl>
                    </UnitSuffix>
                    <FormDescription>{t('deviationAnalysis.volumeResolutionHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name='deviation.volumePlane'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.volumePlane')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='w-full'>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='xy'>{t('deviationAnalysis.volumePlanes.xy')}</SelectItem>
                        <SelectItem value='fitted'>{t('deviationAnalysis.volumePlanes.fitted')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('deviationAnalysis.volumePlaneHelp')}</FormDescription>
                  </FormItem>
                )}
              />
            </div>
          )}
        </Subsection>
      )}
    </AdvancedSection>
  );
}
